import React, { useState } from "react"
import {
  FiBox,
  FiCloud,
  FiCompass,
  FiCpu,
  FiGlobe,
  FiLayers,
  FiPenTool,
  FiSmartphone,
  FiZap,
} from "react-icons/fi"

import Layout from "../components/layout"
import Seo from "../components/seo"
import { HeroMark } from "../components/mark"
import { netlifyFormHandle } from "../utils/form"

const DOMAINS = [
  {
    icon: FiZap,
    title: "AI",
    text: "Products built on LLMs and machine learning: AI agents, assistants, search and the data pipelines behind them.",
  },
  {
    icon: FiCpu,
    title: "Hardware and IoT",
    text: "Software for connected devices: companion apps, device management platforms and the cloud services they report to.",
  },
  {
    icon: FiBox,
    title: "Blockchain and Web3",
    text: "Smart contracts, dApps, wallets and exchange integrations, plus the web and mobile apps around them.",
  },
]

const ORGANIZATION = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Euroweb B.V.",
  url: "https://euroweb.dev",
  logo: "https://euroweb.dev/icons/icon-512x512.png",
  description:
    "Euroweb designs, architects, and ships digital products across web, mobile, AI, and blockchain, backed by CTO advisory.",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Haarlemmerweg 331A",
    postalCode: "1051 LH",
    addressLocality: "Amsterdam",
    addressCountry: "NL",
  },
  identifier: { "@type": "PropertyValue", propertyID: "KvK", value: "81944756" },
  sameAs: ["https://www.linkedin.com/company/euroweb-bv"],
  knowsAbout: [
    "Product engineering",
    "Web development",
    "Mobile app development",
    "AI development",
    "IoT development",
    "Blockchain development",
    "CTO as a service",
    "Fractional CTO",
  ],
}

const PRACTICES = [
  {
    name: "Engineering",
    intro: "We write the code, ship it and keep it running.",
    services: [
      {
        icon: FiGlobe,
        title: "Web development",
        text: "Fast, accessible web apps and platforms, from the first prototype to production scale.",
      },
      {
        icon: FiSmartphone,
        title: "Mobile development",
        text: "Native and cross-platform apps for iOS and Android that feel at home on each.",
      },
      {
        icon: FiCloud,
        title: "DevOps",
        text: "Cloud infrastructure, CI/CD and monitoring, so releasing is routine instead of risky.",
      },
    ],
  },
  {
    name: "Advisory",
    intro: "Senior judgement for the decisions that are expensive to get wrong.",
    services: [
      {
        icon: FiLayers,
        title: "Software architecture",
        text: "System design, technology choices and migration plans that hold up as you grow.",
      },
      {
        icon: FiPenTool,
        title: "Product design",
        text: "Research, UX and interface design that turn requirements into something people want to use.",
      },
      {
        icon: FiCompass,
        title: "CTO as a service",
        text: "A fractional CTO for startups and growing companies: roadmap, hiring, vendor oversight and technical due diligence.",
      },
    ],
  },
]

const Field = ({ label, hint, as: Control = "input", ...props }) => (
  <label className="field">
    <span className="field__label">
      {label}
      {hint && <span className="field__hint"> {hint}</span>}
    </span>
    <Control className="field__control" {...props} />
  </label>
)

const ContactForm = () => {
  const [status, setStatus] = useState("idle")

  const handleSubmit = event => {
    setStatus("sending")
    return netlifyFormHandle(event)
      .then(() => setStatus("sent"))
      .catch(() => setStatus("error"))
  }

  if (status === "sent") {
    return (
      <div className="form-success" role="status">
        <h3>Message sent</h3>
        <p>Thanks for reaching out. We’ll get back to you shortly.</p>
      </div>
    )
  }

  return (
    <form
      className="contact-form"
      onSubmit={handleSubmit}
      action="/"
      method="POST"
      data-netlify="true"
      netlify-honeypot="bot-field"
      name="contact"
    >
      <input type="hidden" value="contact" name="form-name" />
      {/* Honeypot: hidden from people, filled in by bots. Netlify drops
          any submission where it has a value. */}
      <p className="honeypot" aria-hidden="true">
        <label>
          Leave this field empty
          <input name="bot-field" tabIndex={-1} autoComplete="off" />
        </label>
      </p>
      <div className="contact-form__row">
        <Field label="Name" name="name" autoComplete="name" required placeholder="Jane de Vries" />
        <Field
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          required
          placeholder="jane@company.com"
        />
      </div>
      <div className="contact-form__row">
        <Field
          label="Phone"
          hint="(optional)"
          name="phone"
          type="tel"
          autoComplete="tel"
          placeholder="+31 6 1234 5678"
        />
        <Field label="Subject" name="subject" required placeholder="Mobile app for our clinics" />
      </div>
      <Field
        label="Message"
        as="textarea"
        name="message"
        rows={5}
        required
        placeholder="What are you building, and where do you need help?"
      />
      {status === "error" && (
        <p className="form-error" role="alert">
          Your message wasn’t sent. Check your connection and try again.
        </p>
      )}
      <div className="contact-form__actions">
        <button className="btn btn--primary" type="submit" disabled={status === "sending"}>
          {status === "sending" ? "Sending…" : "Send message"}
        </button>
      </div>
    </form>
  )
}

const IndexPage = () => (
  <Layout>
    <Seo
      title="Product Engineering for Web, Mobile & AI"
      path="/"
      jsonLd={ORGANIZATION}
    />

    <section className="hero">
      <div className="hero__visual">
        <div className="hero__glow" aria-hidden="true" />
        <HeroMark />
      </div>
      <h1 className="hero__title">Full-spectrum product engineering.</h1>
      <p className="hero__lede">
        We design, architect, and ship cutting-edge digital products across web,
        mobile, AI, and blockchain. All backed by{" "}
        <span className="nowrap">executive-tier</span> CTO advisory that takes
        you from concept to production at scale.
      </p>
      <div className="hero__actions">
        <a className="btn btn--primary" href="#contact">
          Start a project
        </a>
        <a className="btn btn--glass" href="#services">
          See our services
        </a>
      </div>
    </section>

    <section className="section">
      <header className="section__head">
        <h2>AI, IoT and blockchain development</h2>
      </header>
      <ul className="domains">
        {DOMAINS.map(({ icon: Icon, title, text }) => (
          <li className="domain glass" key={title}>
            <span className="service__icon" aria-hidden="true">
              <Icon />
            </span>
            <h3 className="domain__title">{title}</h3>
            <p className="domain__text">{text}</p>
          </li>
        ))}
      </ul>
    </section>

    <section id="services" className="section">
      <header className="section__head">
        <h2>What we do</h2>
        <p>
          Engineering and advisory, engaged separately or together.
        </p>
      </header>
      <div className="practices">
        {PRACTICES.map(practice => (
          <article className="practice glass" key={practice.name}>
            <h3 className="practice__name">{practice.name}</h3>
            <p className="practice__intro">{practice.intro}</p>
            <ul className="services">
              {practice.services.map(({ icon: Icon, title, text }) => (
                <li className="service" key={title}>
                  <span className="service__icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <div>
                    <h4 className="service__title">{title}</h4>
                    <p className="service__text">{text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </section>

    <section id="contact" className="section">
      <div className="contact glass">
        <div className="contact__intro">
          <h2>Tell us what you’re building</h2>
          <p>
            A few lines about your product, timeline and team are enough to
            start the conversation.
          </p>
        </div>
        <ContactForm />
      </div>
    </section>
  </Layout>
)

export default IndexPage
